import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetExportConfigs,
  useUpdateExportConfig,
  useDeleteExportConfig,
} from "../api";
import { useModal } from "@/shared/lib/modal/modal-context";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";
import { ML_EXPORTS_URL } from "../config/ml-exports-urls";
import { ClassificationExportConfig, ClassificationExportConfigUpdateInput } from "../types/config";
import { Button } from "@/ui/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { Switch } from "@/ui/components/ui/switch";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

const ExportConfigsPage = () => {
  const { t } = useTranslation("mlExports");
  const { selectedOrganizationId } = useOrganizationStore();
  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(selectedOrganizationId!);

  useSetBreadcrumbs([
    {
      path: ML_EXPORTS_URL.ROOT,
      breadcrumb: orgData?.data?.name || t("breadcrumbs.organization"),
      type: "organizations",
      currentId: selectedOrganizationId!,
      isLoading: isOrgLoading,
    },
    {
      path: ML_EXPORTS_URL.CONFIGS,
      breadcrumb: t("breadcrumbs.configs"),
    },
  ]);

  const { data: configs, isLoading } = useGetExportConfigs(
    selectedOrganizationId ? { organization: selectedOrganizationId } : undefined,
    !!selectedOrganizationId
  );
  const { openModal } = useModal();
  const updateConfig = useUpdateExportConfig();
  const deleteConfig = useDeleteExportConfig();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ClassificationExportConfigUpdateInput>({});

  const handleCreate = () => {
    if (!selectedOrganizationId) return;
    openModal("create-export-config", { organizationId: selectedOrganizationId });
  };

  const handleEdit = (config: ClassificationExportConfig) => {
    setEditingId(config.id);
    setEditForm({
      destination_id: config.destination_id,
      export_only_valid_frames: config.export_only_valid_frames,
      export_only_model_mistakes: config.export_only_model_mistakes,
      min_confidence: config.min_confidence,
      max_confidence: config.max_confidence,
    });
  };

  const handleSave = async (id: number) => {
    try {
      await updateConfig.mutateAsync({ id, input: editForm });
      setEditingId(null);
      toast.success(t("configs.updated"));
    } catch {
      toast.error(t("configs.updateFailed"));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteConfig.mutateAsync(id);
      toast.success(t("configs.deleted"));
    } catch {
      toast.error(t("configs.deleteFailed"));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("configs.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("configs.description")}
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          {t("configs.newConfig")}
        </Button>
      </div>

      {configs && configs.length === 0 && (
        <p className="text-muted-foreground">{t("configs.empty")}</p>
      )}

      {configs?.map((config) => {
        const isEditing = editingId === config.id;

        return (
          <Card key={config.id}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  {t("configs.cardTitle", { id: config.id })}
                  {/*
                   * The destination is shown by id, not label: `destination_id` is a
                   * numeric FK while GET /destinations/ is keyed by the `destination`
                   * code string (see ExportDestination) \u2014 the API exposes no join key
                   * between them, so the label cannot be resolved on the FE. Restore
                   * the label once the config or destinations payload carries a
                   * shared identifier.
                   */}
                  {config.destination_id != null &&
                    t("configs.destinationSuffix", { id: config.destination_id })}
                </CardTitle>
                <CardDescription>
                  {t("configs.scope", {
                    org: config.organization_id,
                    location: config.location_id,
                  })}
                  {config.machine_id &&
                    t("configs.scopeMachine", { machine: config.machine_id })}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button size="sm" onClick={() => handleSave(config.id)} disabled={updateConfig.isPending}>
                      {t("configs.save")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                      {t("configs.cancel")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => handleEdit(config)}>
                      {t("configs.edit")}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(config.id)}
                      disabled={deleteConfig.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            {isEditing && (
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>{t("configs.onlyValidFrames")}</Label>
                  <Switch
                    checked={editForm.export_only_valid_frames ?? false}
                    onCheckedChange={(checked) =>
                      setEditForm((prev) => ({ ...prev, export_only_valid_frames: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>{t("configs.onlyModelMistakes")}</Label>
                  <Switch
                    checked={editForm.export_only_model_mistakes ?? false}
                    onCheckedChange={(checked) =>
                      setEditForm((prev) => ({ ...prev, export_only_model_mistakes: checked }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("configs.minConfidence")}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={editForm.min_confidence ?? ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          min_confidence: e.target.value ? parseFloat(e.target.value) : null,
                        }))
                      }
                      placeholder="0.0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("configs.maxConfidence")}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={editForm.max_confidence ?? ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          max_confidence: e.target.value ? parseFloat(e.target.value) : null,
                        }))
                      }
                      placeholder="1.0"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default ExportConfigsPage;
